/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";

import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftPagination from "components/SoftPagination";
import SoftTypography from "components/SoftTypography";
import DataTable from "../Tables/DataTable";
import FlagLoader from "layouts/flagloader";

import UserFilters from "layouts/interviewSelected/components/useFilters";
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions";
import {
  getAttendanceGapReasons,
  cutAttendanceGapSalary,
  exportAttendanceGapReasons,
} from "api/attendance";

const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const cleanParams = (obj) => {
  const params = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== "" && value !== "all" && value !== null && value !== undefined) {
      params[key] = value;
    }
  });

  return params;
};

const formatDate = (value, withTime = false) => {
  if (!value) return "-";

  const normalizedValue =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00`
      : String(value).replace(" ", "T");
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

const formatTime = (value) => {
  if (!value) return "";

  const stringValue = String(value);
  if (/^\d{2}:\d{2}/.test(stringValue)) {
    return stringValue.slice(0, 5);
  }

  const normalizedValue = stringValue.replace(" ", "T");
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return stringValue;

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatShiftTime = (shift) => {
  if (!shift) return "-";

  const start = formatTime(shift.start_time);
  const end = formatTime(shift.end_time);

  return start && end ? `${start} - ${end}` : "-";
};

const getGapStatus = (gap) => {
  if (gap.salary_cut_done) {
    return { label: "Salary Cut", value: "cut", color: "success" };
  }

  if (!gap.is_submitted) {
    return { label: "Reason Pending", value: "not_submitted", color: "warning" };
  }

  if (gap.can_cut_salary || gap.show_button) {
    return { label: "Ready To Cut", value: "available", color: "info" };
  }

  if (!gap.has_to_date_attendance) {
    return { label: "Attendance Pending", value: "pending", color: "warning" };
  }

  return { label: `Available ${formatDate(gap.button_available_from)}`, value: "pending", color: "default" };
};

const statusOptions = [
  { value: "", label: "All Status" },
  { value: "available", label: "Ready To Cut" },
  { value: "cut", label: "Salary Cut" },
  { value: "pending", label: "Pending" },
  { value: "not_submitted", label: "Reason Pending" },
];

function GapReasons() {
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchSubBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  const [gapReasons, setGapReasons] = useState([]);
  const [filters, setFilters] = useState({
    branch_id: "",
    subbranch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "all",
    from: "",
    to: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
  });

  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedGap, setSelectedGap] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const buildParams = useCallback((includePagination = true) => {
    const { from, to, ...restFilters } = filters;
    const search = debouncedSearch?.trim();

    const params = {
      ...restFilters,
      from,
      to,
      from_date: from,
      to_date: to,
      q: search,
      search,
    };

    if (includePagination) {
      params.page = pagination.current_page;
      params.per_page = pagination.per_page;
    }

    return cleanParams(params);
  }, [filters, debouncedSearch, pagination.current_page, pagination.per_page]);

  const fetchGapReasons = useCallback(
    async (initial = false) => {
      try {
        initial ? setPageLoading(true) : setTableLoading(true);

        const response = await getAttendanceGapReasons(buildParams());
        const paginator = response?.data || {};
        const data = Array.isArray(paginator?.data)
          ? paginator.data
          : Array.isArray(response?.data)
            ? response.data
            : [];

        setGapReasons(data);

        if (paginator?.current_page) {
          setPagination((previous) => ({
            ...previous,
            current_page: paginator.current_page,
            last_page: paginator.last_page || 1,
            total: paginator.total || data.length,
            per_page: paginator.per_page || previous.per_page,
          }));
        }
      } catch (error) {
        setMessage({
          severity: "error",
          text: error.message || "Failed to fetch gap reasons.",
        });
        setGapReasons([]);
      } finally {
        setPageLoading(false);
        setTableLoading(false);
      }
    },
    [buildParams]
  );

  useEffect(() => {
    fetchGapReasons(pageLoading);
  }, [fetchGapReasons]);

  const handleFilterChange = (key, value) => {
    setFilters((previous) => (previous[key] === value ? previous : { ...previous, [key]: value }));
    setPagination((previous) => ({ ...previous, current_page: 1 }));
  };

  const filteredGapReasons = useMemo(() => {
    if (!filters.status || filters.status === "all") return gapReasons;
    return gapReasons.filter((gap) => getGapStatus(gap).value === filters.status);
  }, [gapReasons, filters.status]);

  const handleOpenCutDialog = (gap) => {
    setSelectedGap(gap);
    setRemarks("");
  };

  const handleCutSalary = async () => {
    if (!selectedGap) return;

    try {
      setActionLoading(selectedGap.id);
      await cutAttendanceGapSalary(selectedGap.id, { remarks });

      setMessage({
        severity: "success",
        text: "Salary cut recorded successfully.",
      });
      setSelectedGap(null);
      setRemarks("");
      fetchGapReasons(false);
    } catch (error) {
      setMessage({
        severity: "error",
        text: error.message || "Failed to cut salary.",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const blob = await exportAttendanceGapReasons(buildParams(false));
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `gap_reasons_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({
        severity: "error",
        text: error.message || "Failed to export gap reasons.",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { Header: "Employee", accessor: "employee" },
      { Header: "Branch", accessor: "branch" },
      { Header: "Sub Branch", accessor: "subBranch" },
      { Header: "Shift Time", accessor: "shiftTime" },
      { Header: "Last Attendance", accessor: "fromDate" },
      { Header: "Current Attendance", accessor: "toDate" },
      { Header: "Days", accessor: "missingDays" },
      { Header: "Reason", accessor: "reason" },
      { Header: "Status", accessor: "status" },
      { Header: "Cut By", accessor: "cutBy" },
      { Header: "Cut Date", accessor: "cutDate" },
      { Header: "Action", accessor: "action" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      filteredGapReasons.map((gap) => {
        const status = getGapStatus(gap);
        const user = gap.user || {};
        const branch = gap.branch || user.branch;
        const subBranch = gap.sub_branch || gap.subBranch || user.sub_branch || user.subBranch;
        const shift = gap.shift || user.shift || user.primary_shift;
        const canCut = !gap.salary_cut_done && (gap.can_cut_salary || gap.show_button);

        return {
          employee: (
            <SoftBox>
              <SoftTypography variant="button" fontWeight="medium">
                {user.name || "-"}
              </SoftTypography>
              <SoftTypography variant="caption" color="text" display="block">
                {user.user_code || user.email || "-"}
              </SoftTypography>
            </SoftBox>
          ),
          branch: branch?.name || user.branch_name || user.branch_id || "-",
          subBranch: subBranch?.name || user.sub_branch_name || user.subbranch_id || "-",
          shiftTime: gap.shift_time || formatShiftTime(shift),
          fromDate: formatDate(gap.from_date),
          toDate: formatDate(gap.to_date),
          missingDays: gap.missing_days ?? "-",
          reason: (
            <Tooltip title={gap.reason || ""}>
              <SoftTypography
                variant="button"
                color="text"
                sx={{
                  display: "block",
                  maxWidth: 220,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {gap.reason || "-"}
              </SoftTypography>
            </Tooltip>
          ),
          status: <Chip size="small" label={status.label} color={status.color} variant="outlined" />,
          cutBy: gap.salary_cut_by ? (
            <SoftTypography variant="button" fontWeight="medium">
              {gap.salary_cut_by.name || "-"}
            </SoftTypography>
          ) : (
            "-"
          ),
          cutDate: gap.salary_cut_at ? formatDate(gap.salary_cut_at, true) : "-",
          action: canCut ? (
            <SoftButton
              size="small"
              color="error"
              variant="gradient"
              disabled={actionLoading === gap.id}
              onClick={() => handleOpenCutDialog(gap)}
            >
              {actionLoading === gap.id ? "Cutting..." : "Cut Salary"}
            </SoftButton>
          ) : (
            <SoftTypography variant="caption" color="text">
              {gap.salary_cut_done ? "Done" : "No Action"}
            </SoftTypography>
          ),
        };
      }),
    [filteredGapReasons, actionLoading]
  );

  const getPaginationRange = () => {
    const total = pagination.last_page;
    const current = pagination.current_page;
    const limit = 5;

    let start = Math.max(1, current - Math.floor(limit / 2));
    let end = start + limit - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - limit + 1);
    }

    const pages = [];

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    if (end < total) {
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  if (pageLoading) {
    return (
      <SoftBox p={3} textAlign="center">
        <FlagLoader />
      </SoftBox>
    );
  }

  return (
    <Card>
      <SoftBox py={1}>
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dropdownOptions={dropdownOptions}
          loading={dropdownLoading}
          fetchBranches={fetchBranches}
          fetchSubBranches={fetchSubBranches}
          fetchShifts={fetchShifts}
          fetchDepartments={fetchDepartments}
          fetchDesignations={fetchDesignations}
          fetchRoles={fetchRoles}
          showHistoryFilters
          pageContext="Attendance.GapReasons"
          statusOptions={statusOptions}
          historyActions={
            <SoftButton
              size="small"
              color="info"
              variant="gradient"
              disabled={exportLoading}
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
            >
              {exportLoading ? "Exporting..." : "Export Excel"}
            </SoftButton>
          }
        />

        <Grid container spacing={2} alignItems="center" justifyContent="space-between" mt={1} mb={-1}>
          <Grid item>
            <SoftTypography variant="h6" color="text">
              Gap Reasons ({pagination.total})
            </SoftTypography>
          </Grid>
        </Grid>

        {tableLoading ? (
          <SoftBox py={4} textAlign="center">
            <FlagLoader />
          </SoftBox>
        ) : (
          <>
            <DataTable table={{ columns, rows }} entriesPerPage={false} canSearch={false} showTotalEntries />
            {rows.length === 0 && (
              <SoftBox py={3} textAlign="center">
                <SoftTypography variant="button" color="text">
                  No gap reasons found.
                </SoftTypography>
              </SoftBox>
            )}
          </>
        )}

        {pagination.last_page > 1 && (
          <SoftBox mt={2} display="flex" justifyContent="center">
            <SoftPagination variant="gradient" color="info">
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((previous) => ({ ...previous, current_page: 1 }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {"<<"}
              </SoftPagination>

              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((previous) => ({
                    ...previous,
                    current_page: previous.current_page - 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {"<"}
              </SoftPagination>

              {getPaginationRange().map((item, index) =>
                item === "..." ? (
                  <SoftPagination key={`${item}-${index}`} item disabled>
                    ...
                  </SoftPagination>
                ) : (
                  <SoftPagination
                    key={item}
                    item
                    active={pagination.current_page === item}
                    onClick={() => {
                      setPagination((previous) => ({ ...previous, current_page: item }));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    {item}
                  </SoftPagination>
                )
              )}

              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((previous) => ({
                    ...previous,
                    current_page: previous.current_page + 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {">"}
              </SoftPagination>

              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((previous) => ({
                    ...previous,
                    current_page: pagination.last_page,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                {">>"}
              </SoftPagination>
            </SoftPagination>
          </SoftBox>
        )}
      </SoftBox>

      <Dialog open={Boolean(selectedGap)} onClose={() => setSelectedGap(null)} fullWidth maxWidth="sm">
        <DialogTitle>Cut Salary</DialogTitle>
        <DialogContent>
          <SoftTypography variant="button" color="text">
            This will record salary cut for the selected attendance gap.
          </SoftTypography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            label="Remarks"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <SoftButton color="secondary" variant="outlined" onClick={() => setSelectedGap(null)}>
            Cancel
          </SoftButton>
          <SoftButton
            color="error"
            variant="gradient"
            disabled={actionLoading === selectedGap?.id}
            onClick={handleCutSalary}
          >
            {actionLoading === selectedGap?.id ? "Cutting..." : "Confirm Cut"}
          </SoftButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={5000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={message?.severity || "info"}
          onClose={() => setMessage(null)}
          sx={{ width: "100%" }}
        >
          {message?.text}
        </Alert>
      </Snackbar>
    </Card>
  );
}

export default GapReasons;
